import type { Pet } from "../data/pets";

const escapeXml = (value: string) =>
  value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");

function speciesDetails(pet: Pet): string {
  const { primary, secondary, accent, ink } = pet.palette;

  switch (pet.species) {
    case "fox":
      return `
        <path d="M52 48 73 20 82 58Z" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
        <path d="M204 48 183 20 174 58Z" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
        <path d="M61 44 72 31 75 51Z" fill="${accent}"/>
        <path d="M195 44 184 31 181 51Z" fill="${accent}"/>
        <path d="M60 178C22 164 17 204 48 214 29 223 47 247 79 226" fill="none" stroke="${primary}" stroke-width="24" stroke-linecap="square"/>
      `;
    case "bunny":
      return `
        <rect x="63" y="7" width="43" height="74" rx="20" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
        <rect x="150" y="7" width="43" height="74" rx="20" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
        <rect x="76" y="19" width="17" height="46" rx="8" fill="${accent}"/>
        <rect x="163" y="19" width="17" height="46" rx="8" fill="${accent}"/>
      `;
    case "owl":
      return `
        <path d="M63 68 38 39 92 51Z" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
        <path d="M193 68 218 39 164 51Z" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
        <circle cx="92" cy="108" r="35" fill="${secondary}"/>
        <circle cx="164" cy="108" r="35" fill="${secondary}"/>
        <path d="m128 113-15 18h30Z" fill="${accent}" stroke="${ink}" stroke-width="4"/>
      `;
    case "bot":
      return `
        <line x1="128" y1="48" x2="128" y2="23" stroke="${ink}" stroke-width="7"/>
        <rect x="119" y="12" width="18" height="18" fill="${accent}" stroke="${ink}" stroke-width="5"/>
        <rect x="52" y="67" width="152" height="104" rx="18" fill="${primary}" stroke="${ink}" stroke-width="7"/>
        <rect x="69" y="83" width="118" height="70" rx="10" fill="${secondary}"/>
      `;
    case "bear":
      return `
        <circle cx="73" cy="62" r="29" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
        <circle cx="183" cy="62" r="29" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
        <circle cx="73" cy="62" r="13" fill="${accent}"/>
        <circle cx="183" cy="62" r="13" fill="${accent}"/>
      `;
    case "cat":
    default:
      return `
        <path d="M53 70 55 22 99 58Z" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
        <path d="M203 70 201 22 157 58Z" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
        <path d="M61 53 62 36 79 51Z" fill="${accent}"/>
        <path d="M195 53 194 36 177 51Z" fill="${accent}"/>
      `;
  }
}

export interface PetArtOptions {
  transparent?: boolean;
  stateIndex?: number;
  frameIndex?: number;
}

function frameTransform(stateIndex: number, frameIndex: number): string {
  const phase = frameIndex % 4;
  const bob = [0, -4, 0, 3][phase];
  const stride = [-5, 0, 5, 0][phase];
  switch (stateIndex) {
    case 1:
      return `translate(${stride} ${bob}) rotate(${stride / 2} 128 176)`;
    case 2:
      return `translate(${256 - stride} ${bob}) scale(-1 1) rotate(${stride / 2} 128 176)`;
    case 3:
      return `translate(0 ${bob}) rotate(${[0, -3, 0, 3][phase]} 128 150)`;
    case 4:
      return `translate(0 ${[-2, -22, -8, 0][phase]})`;
    case 5:
      return `translate(0 14) rotate(${[74, 80, 86, 80][phase]} 128 190)`;
    case 6:
      return `translate(0 ${[2, 3, 2, 4][phase]}) scale(${[1, 1.01, 1, .99][phase]})`;
    case 7:
      return `translate(${stride * 1.6} ${bob}) rotate(${stride / 1.8} 128 176)`;
    case 8:
      return `translate(0 ${bob}) scale(${[1, 1.025, 1, .98][phase]})`;
    case 9:
      return `translate(${[0, 3, 6, 3][phase]} ${bob})`;
    case 10:
      return `translate(${[0, -3, -6, -3][phase]} ${bob})`;
    default:
      return `translate(0 ${bob})`;
  }
}

export function renderPetSvg(pet: Pet, options: PetArtOptions = {}): string {
  const { background, primary, secondary, accent, ink } = pet.palette;
  const robotFace = pet.species === "bot";
  const stateIndex = options.stateIndex ?? 0;
  const frameIndex = options.frameIndex ?? 0;
  const backgroundMarkup = options.transparent
    ? ""
    : `<rect width="256" height="256" fill="${background}"/>
  <path d="M0 32H256M0 64H256M0 96H256M0 128H256M0 160H256M0 192H256M0 224H256M32 0V256M64 0V256M96 0V256M128 0V256M160 0V256M192 0V256M224 0V256" stroke="${ink}" stroke-opacity=".045"/>`;

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" role="img" aria-labelledby="title description" shape-rendering="crispEdges">
  <title id="title">${escapeXml(pet.name)}</title>
  <desc id="description">${escapeXml(pet.description)}</desc>
  ${backgroundMarkup}
  <g transform="${frameTransform(stateIndex, frameIndex)}">
  <ellipse cx="128" cy="226" rx="72" ry="15" fill="${ink}" opacity=".12"/>
  ${speciesDetails(pet)}
  ${robotFace ? "" : `<rect x="54" y="54" width="148" height="126" rx="61" fill="${primary}" stroke="${ink}" stroke-width="7"/>`}
  <rect x="71" y="145" width="114" height="90" rx="40" fill="${primary}" stroke="${ink}" stroke-width="7"/>
  <rect x="59" y="166" width="35" height="55" rx="15" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
  <rect x="162" y="166" width="35" height="55" rx="15" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
  <rect x="88" y="211" width="31" height="28" rx="9" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
  <rect x="137" y="211" width="31" height="28" rx="9" fill="${secondary}" stroke="${ink}" stroke-width="6"/>
  <rect x="81" y="96" width="24" height="26" rx="8" fill="${ink}"/>
  <rect x="151" y="96" width="24" height="26" rx="8" fill="${ink}"/>
  <rect x="87" y="99" width="7" height="8" fill="white" opacity=".86"/>
  <rect x="157" y="99" width="7" height="8" fill="white" opacity=".86"/>
  <rect x="120" y="126" width="16" height="11" rx="4" fill="${accent}" stroke="${ink}" stroke-width="4"/>
  <path d="M104 143c9 12 39 12 48 0" fill="none" stroke="${ink}" stroke-width="5" stroke-linecap="round"/>
  <rect x="103" y="168" width="50" height="24" rx="8" fill="${accent}" stroke="${ink}" stroke-width="5"/>
  <path d="m113 180 9 7 20-17" fill="none" stroke="${ink}" stroke-width="5" stroke-linecap="square"/>
  </g>
</svg>`;
}

export function petSvgDataUri(pet: Pet): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(renderPetSvg(pet))}`;
}
